import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { HttpError, HttpService } from 'src/toolkit';
import { TTableSort, TableSort } from 'src/components/table/thead/col';

import { useAppSelector } from '../../hooks';
import { UserClientSortCol, TUserClientList, TUserClientListResponse, TUserClientQuery, TUserClientListSortCol, adaptUserClientFromListResponse, adaptUserClientListQueryToRequest } from '../../types';
import { BACKEND_USER_CLIENT } from '../../backend-paths';


export const userClientListGetApi = createAsyncThunk(
	BACKEND_USER_CLIENT,
	(payload: TUserClientQuery | undefined, thunkApi) => {
		const params = payload ? adaptUserClientListQueryToRequest(payload) : {};

		return HttpService.get<TUserClientListResponse>(BACKEND_USER_CLIENT, { params }).then(result => {
			return adaptUserClientFromListResponse(result.data);
		}).catch((error: HttpError) => {
			return thunkApi.rejectWithValue({ ...error });
		});
	},
);

type TInitialState = {
	isLoading: boolean,
	list: TUserClientList,
	query: TUserClientQuery,
	error?: HttpError,
};

const initialState: TInitialState = {
	isLoading: false,
	list: {
		items: [],
		count: 0,
	},
	query: {
		limit: 10,
		offset: 0,
		search: undefined,
		sort: TableSort.ASC,
		sortCol: UserClientSortCol.CREATED_AT,
	},
	error: undefined,
};

export const userClientListSlice = createSlice({
	name: 'userClientListSlice',
	initialState,
	reducers: {
		setUserClientListQueryLimit(state, action: PayloadAction<number>) {
			state.query.limit = action.payload;
		},

		setUserClientListQueryOffset(state, action: PayloadAction<number>) {
			state.query.offset = action.payload;
		},

		setUserClientListQuerySearch(state, action: PayloadAction<string>) {
			const search = action.payload || undefined;
			const isSearch = search && search.length > 0;

			if (isSearch && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.search = search;
		},

		setUserClientListQuerySort(state, action: PayloadAction<TTableSort>) {
			state.query.sort = action.payload;
		},

		setUserClientListQuerySortCol(state, action: PayloadAction<TUserClientListSortCol>) {
			state.query.sortCol = action.payload;
		},

		clearUserClientList(state) {
			return { ...initialState, list: state.list, query: state.query };
		},
	},

	extraReducers: {
		[userClientListGetApi.pending.type]: (state) => {
			state.isLoading = true;
			state.error = undefined;
		},

		[userClientListGetApi.fulfilled.type]: (state, action: PayloadAction<TUserClientList>) => {
			state.isLoading = false;
			state.list = action.payload;
			state.error = undefined;
		},

		[userClientListGetApi.rejected.type]: (state, action: PayloadAction<HttpError>) => {
			state.isLoading = false;
			state.error = action.payload;
		},
	},
});

export const useUserClientListSelector = () => useAppSelector(state => state[userClientListSlice.name]);

export const { clearUserClientList, setUserClientListQueryLimit, setUserClientListQueryOffset, setUserClientListQuerySort, setUserClientListQuerySortCol, setUserClientListQuerySearch } = userClientListSlice.actions;
